import {
  Button,
  Flex,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
  CTableBody,
} from "@coreui/react";
import { DndContext } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import {
  activitiesByMaintenanceRoutineID,
  getMaintenanceRoutine,
} from "graphql/queries";
import { updateActivity, updateMaintenanceRoutine } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import ActivityCreateForm from "./ActivityCreateForm";
import ActivityUpdateForm from "./ActivityUpdateForm";
import SortableActivity from "components/activities/SortableActivity";

const Activities = ({
  maintenanceRoutineID,
  maintenanceRoutineName,
  visible,
  onClose,
}) => {
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "maintenance-routine:create-act",
    "maintenance-routine:update-act",
    "maintenance-routine:delete-act",
  ]);
  const [visibleState, setVisibleState] = useState(visible);
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activityDetails, setActivityDetails] = useState();
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    activityId: null,
    activityName: null,
  });
  const [modalActivity, setModalActivity] = useState({
    visible: false,
    activityId: null,
  });
  const columnsMapped = useMemo(
    () =>
      activities.map((item) => ({
        ...item,
        type: strings.details.type.options[item.type],
        actions: (
          <>
            <ActionIcon
              name={
                activityDetails?.id === item.id
                  ? strings.actions.hide
                  : strings.actions.show
              }
              icon={
                activityDetails?.id === item.id ? "VisibilityOff" : "Visibility"
              }
              color="primary"
              onClick={() => {
                activityDetails?.id === item.id
                  ? setActivityDetails(null)
                  : setActivityDetails(item);
              }}
            />
            {canUpdate && (
              <ActionIcon
                name={strings.actions.edit}
                icon="Edit"
                color="primary"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canDelete && (
              <ActionIcon
                name={strings.actions.delete}
                icon="Delete"
                color="red.60"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    activityId: item.id,
                    activityName: item.name,
                  });
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activities, activityDetails?.id]
  );

  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchActivities(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) => {
    setVisibleState(false);
    setModalActivity({ visible: true, action, activityId: id });
  };
  const closeModal = () => {
    setVisibleState(true);
    setModalActivity({
      visible: false,
    });
  };
  const onSuccessActivity = () => {
    closeModal();
    fetchActivities();
  };

  const onSort = (from, to) => {
    if (from !== to) {
      let orderUpdated = [];
      setActivities((prevStList) => {
        const newList = [...prevStList];
        const oldIndex = newList.findIndex((item) => item.id === from);
        const oldItem = newList[oldIndex];
        const newIndex = newList.findIndex((item) => item.id === to);

        newList?.splice(oldIndex, 1);
        newList?.splice(newIndex, 0, oldItem);

        orderUpdated = newList?.map((it) => it.id) ?? [];
        return newList;
      });

      sortActivities(orderUpdated);
    }
  };

  async function fetchActivities(token, isNext = true, keywords) {
    try {
      setActivitiesLoading(true);

      const activitiesData = await API.graphql(
        graphqlOperation(activitiesByMaintenanceRoutineID, {
          maintenanceRoutineID,
          limit: !keywords ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      name: { contains: key },
                    },
                    {
                      description: { contains: key },
                    },
                  ],
                })),
                deletedAt: { attributeExists: false },
              }
            : { deletedAt: { attributeExists: false } },
        })
      );
      const activitiesItems =
        activitiesData.data.activitiesByMaintenanceRoutineID.items;

      const maintenanceRoutineData = await API.graphql(
        graphqlOperation(getMaintenanceRoutine, { id: maintenanceRoutineID })
      );
      const activitiesOrder =
        maintenanceRoutineData.data.getMaintenanceRoutine?.activitiesOrder ||
        [];

      setActivities(
        activitiesItems.sort(
          (a, b) =>
            activitiesOrder.indexOf(a.id) - activitiesOrder.indexOf(b.id)
        )
      );
      setActivitiesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setActivitiesLoading(false);
    }
  }
  async function activityDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(updateActivity, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateActivity.id;
      setActivities(activities.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        activityId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }
  async function sortActivities(order) {
    try {
      await API.graphql(
        graphqlOperation(updateMaintenanceRoutine, {
          input: { id: maintenanceRoutineID, activitiesOrder: order },
        })
      );
    } catch (err) {
      console.log("Error Sort Activities", err);
    }
  }

  useEffect(() => {
    fetchActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maintenanceRoutineID]);

  useEffect(() => {
    setVisibleState(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const tableColumns = [
    {
      key: "drag",
      label: "",
      _style: { width: "2%" },
    },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "20%" },
    },
    {
      key: "type",
      label: strings.table.headers.type,
      _style: { width: "20%" },
    },
    {
      key: "abbreviation",
      label: strings.table.headers.abbreviation,
      _style: { width: "10%" },
    },
    {
      key: "unit",
      label: strings.table.headers.unit,
      _style: { width: "10%" },
    },
    {
      key: "defaultValue",
      label: strings.table.headers.defaultValue,
      _style: { width: "20%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "20%" },
    },
  ];

  return (
    <>
      <CModal
        size="xl"
        alignment="center"
        visible={visibleState}
        onClose={() => !modalActivity.visible && onClose()}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>
            {strings.title} - {maintenanceRoutineName}
          </CModalTitle>
        </CModalHeader>
        <CModalBody style={{ overflow: "hidden" }}>
          <ScrollView maxHeight="75vh">
            <div>
              <div className="p-3">
                <Flex className="mb-1" justifyContent="space-between">
                  <div style={styles.searchByKeyword}>
                    <SearchField
                      label={strings.table.search.label}
                      placeholder={strings.table.search.placeholder}
                      onSubmit={onKeywordSubmit}
                      onClear={onKeywordSubmit}
                    />
                  </div>
                  {canCreate && (
                    <Button
                      style={styles.buttonAdd}
                      children={strings.actions.addActivity}
                      variation="primary"
                      onClick={() => openModal("create")}
                    />
                  )}
                </Flex>
                {activitiesLoading ? (
                  <Loader />
                ) : (
                  <>
                    <CTable
                      className="table-centered"
                      columns={tableColumns}
                      tableHeadProps={{ color: "light" }}
                      responsive
                      hover
                    >
                      <CTableBody>
                        <DndContext
                          modifiers={[restrictToVerticalAxis]}
                          onDragEnd={(event) =>
                            onSort(event.active.id, event.over.id)
                          }
                        >
                          <SortableContext
                            items={columnsMapped.map((it) => it.id)}
                          >
                            {columnsMapped?.map((item) => (
                              <SortableActivity item={item} key={item.id} />
                            ))}
                          </SortableContext>
                        </DndContext>
                      </CTableBody>
                    </CTable>
                  </>
                )}
                <CCollapse
                  visible={!!activityDetails?.id}
                  style={styles.collapseCard}
                >
                  <CCard key={activityDetails?.id}>
                    <CCardBody>
                      <h4 className="text-muted">
                        {strings.details.title} "{activityDetails?.name}"
                      </h4>
                      <p className="text-muted">
                        <b>{strings.details.description}:</b>{" "}
                        {activityDetails?.description}
                      </p>
                      <p className="text-muted">
                        <b>{strings.details.type.label}:</b>{" "}
                        {strings.details.type.options[activityDetails?.type]}
                      </p>
                      <p className="text-muted">
                        <b>{strings.details.abbreviation}:</b>{" "}
                        {activityDetails?.abbreviation}
                      </p>
                      <p className="text-muted">
                        <b>{strings.details.unit}:</b> {activityDetails?.unit}
                      </p>
                      <p className="text-muted">
                        <b>{strings.details.defaultValue}:</b>{" "}
                        {activityDetails?.defaultValue}
                      </p>
                      <p className="text-muted">
                        <b>{strings.details.createdAt}:</b>{" "}
                        {moment(activityDetails?.createdAt).format(
                          "YYYY-MM-DD HH:mm:ss"
                        )}
                      </p>
                    </CCardBody>
                  </CCard>
                </CCollapse>
              </div>
              <ConfirmModal
                visible={modalDetails.visible}
                body={
                  <>
                    <span>
                      {strings.formatString(
                        strings.modal.confirmDeleteQuestion,
                        {
                          name: modalDetails.activityName,
                        }
                      )}
                    </span>
                  </>
                }
                onCancel={() =>
                  setModalDetails({
                    visible: false,
                  })
                }
                onConfirm={() => activityDelete(modalDetails.activityId)}
              />
            </div>
          </ScrollView>
        </CModalBody>
      </CModal>
      {modalActivity.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalActivity.visible}
          onClose={closeModal}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalActivity.action === "create" ? (
                <>
                  {strings.modalActivity.addTitle} - {maintenanceRoutineName}
                </>
              ) : (
                <>
                  {strings.modalActivity.updateTitle} - {maintenanceRoutineName}
                </>
              )}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalActivity.action === "create" ? (
                <ActivityCreateForm
                  maintenanceRoutineID={maintenanceRoutineID}
                  onSuccess={onSuccessActivity}
                />
              ) : (
                <ActivityUpdateForm
                  activityId={modalActivity.activityId}
                  onSuccess={onSuccessActivity}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Actividades",
    successDeleted: "Actividad eliminada con éxito",
    actions: {
      addActivity: "Agregar Actividad",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    responses: {
      errorFetch: "Error al consultar actividades",
    },
    table: {
      headers: {
        name: "Nombre",
        type: "Tipo",
        abbreviation: "Abreviatura",
        unit: "Unidad",
        defaultValue: "Valor por defecto",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles de la Actividad",
      type: {
        label: "Tipo",
        options: {
          date: "Fecha",
          number: "Número",
          text: "Texto",
          list: "Lista",
          sign: "Firma",
          check: "Check",
        },
      },
      description: "Descripción",
      abbreviation: "Abreviatura",
      unit: "Unidad",
      defaultValue: "Valor por defecto",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar la actividad {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalActivity: {
      addTitle: "Agregar Actividad",
      updateTitle: "Actualizar Actividad",
    },
  },
});

const styles = {
  buttonAdd: { height: "fit-content" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default Activities;
